import React, { useState, useEffect, useRef } from 'react';
import { GifContainer } from './GifContainer';
import { FeatureGrid } from './FeatureGrid';
import { Sparkles, CreditCardIcon, Brain, MessageSquare, TrendingUp, Zap, Target, Users, Clock, Shield } from 'lucide-react';
import { trackSectionView, trackButtonClick } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useMetaPixel } from '../../hooks/useMetaPixel';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';

export function Features() {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const { trackButtonClick: trackMetaPixelButton, trackLead } = useMetaPixel();
  const { trackButtonClick: trackAnalyticsButton, trackConversion } = useAnalyticsTracking();
  
  // Track section view when Features component mounts and handle animations
  useEffect(() => {
    trackSectionView('Features Section');
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Staggered animation timeline with faster timing
          setTimeout(() => setAnimationPhase(1), 50);  // Title - reduced from 100ms
          setTimeout(() => setAnimationPhase(2), 150); // Description - reduced from 300ms
          setTimeout(() => setAnimationPhase(3), 250); // GIF Container - reduced from 500ms
          setTimeout(() => setAnimationPhase(4), 350); // Feature Grid - reduced from 800ms
          setTimeout(() => setAnimationPhase(5), 450); // CTA Buttons - reduced from 1000ms
          
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Trigger earlier when only 10% of section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleClaimFreeInterview = () => {
    trackButtonClick('Claim Free Interview', 'Features Section');
    trackMetaPixelButton('Claim Free Interview', 'Features Section');
    trackLead(); // Track as lead generation
    
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
      // Open the app page in a new tab
      window.open(`${platformUrl}/login?utm_source=landing_page&utm_medium=features_section&utm_campaign=free_interview`, '_blank');
  };

  const handleSignUpFree = () => {
    // Comprehensive analytics tracking
    trackAnalyticsButton('Sign Up & Get Free Interview', 'Features Section', {
      button_type: 'primary_cta',
      button_position: 'features_main',
      conversion_intent: 'sign_up',
      offer_type: 'free_interview',
      utm_source: 'features_section',
      utm_medium: 'cta_button',
      utm_campaign: 'free_interview'
    });
    
    // Track conversion
    trackConversion('sign_up', {
      source: 'features_section',
      button_name: 'Sign Up & Get Free Interview',
      offer_type: 'free_interview'
    });
    
    // Keep existing Firebase tracking for backward compatibility
    trackButtonClick('Sign Up & Get Free Interview', 'Features');
    
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    window.open(`${platformUrl}/login?utm_source=features_section&utm_medium=cta_button&utm_campaign=free_interview`, '_blank');
  };

  const handleViewPricing = () => {
    // Comprehensive analytics tracking
    trackAnalyticsButton('View Pricing', 'Features Section', {
      button_type: 'secondary_cta',
      button_position: 'features_secondary',
      conversion_intent: 'pricing_view',
      utm_source: 'features_section',
      utm_medium: 'cta_button',
      utm_campaign: 'view_pricing'
    });
    
    // Track conversion
    trackConversion('pricing_view', {
      source: 'features_section',
      button_name: 'View Pricing'
    });
    
    // Keep existing Firebase tracking for backward compatibility
    trackButtonClick('View Pricing', 'Features');
    
    navigate('/pricing');
  };

  return (
    <section 
      ref={sectionRef}
      id="features" 
      className="py-32 relative overflow-hidden"
    >
      {/* Background Effects with animations */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 opacity-0 transition-opacity duration-700" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-700 delay-150" 
        data-speed="0.05"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(16,185,129,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-700 delay-200" 
        data-speed="0.07"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      
      {/* Floating particles */}
      <div 
        className="absolute left-[20%] top-[15%] w-5 h-5 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float parallax-element opacity-0 transition-opacity duration-700 delay-250" 
        data-speed="0.13"
        style={{ opacity: isVisible ? 0.6 : 0, animationDelay: '0.5s', animationDuration: '6s' }}
      />
      <div 
        className="absolute right-[25%] top-[30%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float parallax-element opacity-0 transition-opacity duration-700 delay-300" 
        data-speed="0.10"
        style={{ opacity: isVisible ? 0.7 : 0, animationDelay: '0.3s', animationDuration: '5s' }}
      />
      
      {/* Diagonal accent lines */}
      <div 
        className="absolute -left-32 top-52 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-primary-light/15 to-transparent rotate-[25deg] parallax-element opacity-0 transition-opacity duration-700 delay-200" 
        data-speed="0.08"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute -right-32 top-96 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-secondary-light/15 to-transparent -rotate-[25deg] parallax-element opacity-0 transition-opacity duration-700 delay-300" 
        data-speed="0.09"
        style={{ opacity: isVisible ? 1 : 0 }}
      />

      <div className="container mx-auto relative px-4 md:px-0">
        <div className="text-center mb-16 md:mb-20">
          <h2 
            className={`text-3xl md:text-4xl font-bold mb-6 text-text-primary-light dark:text-text-primary-dark transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
          Why MockTrail is the Best AI Interview Coach
          </h2>
          <p 
            className={`text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Join <span className='text-text-primary-light dark:text-text-secondary-dark font-medium'>50,000+ job seekers</span> who improved their interview skills with MockTrail's <span className="text-text-primary-light dark:text-text-primary-dark">AI-powered mock interviews</span>. Get realistic practice sessions, instant feedback, and personalized coaching tailored to your industry and role.
          </p>
        </div>

        <div 
          className={`max-w-6xl mx-auto mb-20 md:mb-24 transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            animationPhase >= 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <GifContainer />
        </div>

        <div 
          className={`transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            animationPhase >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <FeatureGrid />
        </div>

        {/* CTA Buttons */}
        <div 
          className={`flex flex-col sm:flex-row gap-4 justify-center items-center mt-16 transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            animationPhase >= 5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button 
            onClick={handleSignUpFree}
            className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto relative overflow-hidden group py-4"
          >
            {/* Button shine effect */}
            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 -translate-x-full group-hover:translate-x-full transition-all duration-800 ease-out"></span>

            <Sparkles className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
            Sign Up & Get Free Interview
          </button>
          <button 
            onClick={handleViewPricing}
            className="btn-secondary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <CreditCardIcon className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:scale-110" />
            View Pricing
          </button>
        </div>
      </div>
    </section>
  );
}